import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import styles from "./FoodCheckoutInterface.module.css";
import { client } from "../../appSync/appSyncClient";
import { gql } from "graphql-request";

interface Ticket {
  paymentId: string;
  status: string;
  email?: string;
  ticketCount?: number;
  vegDinner?: number;
  nonVegDinner?: number;
  checkedIn?: boolean;
  checkedInAt?: string;
  foodCheckedOut?: boolean;
  foodCheckedOutAt?: string;
}

interface VerifyTicketResponse {
  verifyTicket: {
    success: boolean;
    ticket?: Ticket;
    message: string;
  };
}

interface CheckOutFoodResponse {
  checkOutFood: {
    success: boolean;
    ticket?: Ticket;
    message: string;
  };
}

const VERIFY_TICKET_QUERY = gql`
  query VerifyTicket($paymentId: String!) {
    verifyTicket(paymentId: $paymentId) {
      success
      message
      ticket {
        paymentId
        status
        email
        ticketCount
        vegDinner
        nonVegDinner
        checkedIn
        checkedInAt
        foodCheckedOut
        foodCheckedOutAt
      }
    }
  }
`;

const CHECK_OUT_FOOD_MUTATION = gql`
  mutation CheckOutFood($paymentId: String!) {
    checkOutFood(paymentId: $paymentId) {
      success
      message
      ticket {
        paymentId
        status
        email
        ticketCount
        vegDinner
        nonVegDinner
        checkedIn
        checkedInAt
        foodCheckedOut
        foodCheckedOutAt
      }
    }
  }
`;

const FoodCheckoutInterface: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [paymentId, setPaymentId] = useState("");
  const [ticket, setTicket] = useState<Ticket | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState<"success" | "error" | "info">(
    "info",
  );

  useEffect(() => {
    const idFromUrl = searchParams.get("id");
    if (idFromUrl) {
      setPaymentId(idFromUrl);
    }
  }, [searchParams]);

  useEffect(() => {
    if (paymentId && searchParams.get("id")) {
      handleVerifyTicket();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentId]);

  const handleVerifyTicket = async () => {
    if (!paymentId.trim()) {
      setMessage("Please enter or scan a payment ID");
      setMessageType("error");
      return;
    }

    setIsLoading(true);
    setMessage("");

    try {
      const result = (await client.request(VERIFY_TICKET_QUERY, {
        paymentId: paymentId.trim(),
      })) as VerifyTicketResponse;

      console.log("Verify Response:", result.verifyTicket);
      console.log("Ticket for Food Checkout:", result.verifyTicket.ticket);
      const { success, ticket, message: responseMessage } = result.verifyTicket;

      if (success && ticket) {
        if (!ticket.vegDinner && !ticket.nonVegDinner) {
          setTicket(null);
          setMessage("No dinner items on this ticket.");
          setMessageType("error");
        } else {
          setTicket(ticket);
          setMessage(responseMessage);
          setMessageType("success");
        }
      } else {
        setTicket(null);
        setMessage(responseMessage || "Failed to verify ticket");
        setMessageType("error");
      }
    } catch (err) {
      console.error("Error verifying ticket:", err);
      setMessage("Error verifying ticket. Please try again.");
      setMessageType("error");
      setTicket(null);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCheckOutFood = async () => {
    if (!ticket?.paymentId) {
      setMessage("No ticket loaded");
      setMessageType("error");
      return;
    }

    setIsLoading(true);
    setMessage("");

    try {
      const result = (await client.request(CHECK_OUT_FOOD_MUTATION, {
        paymentId: ticket.paymentId,
      })) as CheckOutFoodResponse;

      const {
        success,
        ticket: updatedTicket,
        message: responseMessage,
      } = result.checkOutFood;

      if (success && updatedTicket) {
        setTicket(updatedTicket);
        setMessage(responseMessage);
        setMessageType("success");
      } else {
        setMessage(responseMessage || "Failed to check out food");
        setMessageType("error");
      }
    } catch (err) {
      console.error("Error checking out food:", err);
      setMessage("Error checking out food. Please try again.");
      setMessageType("error");
    } finally {
      setIsLoading(false);
    }
  };

  const handleClear = () => {
    setPaymentId("");
    setTicket(null);
    setMessage("");
  };

  return (
    <div className={styles.container}>
      <div className={styles.card}>
        <h1 className={styles.title}>Dinner Food Checkout</h1>

        <div className={styles.inputSection}>
          <label htmlFor="paymentId" className={styles.label}>
            Payment ID / Confirmation Number
          </label>
          <p className={styles.hint}>
            Scan the dinner QR code on your ticket or enter the confirmation
            number
          </p>
          <input
            id="paymentId"
            type="text"
            value={paymentId}
            onChange={(e) => setPaymentId(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleVerifyTicket()}
            placeholder="e.g., bce90f6c-9556-410b-aa35-772e2aa145f9"
            className={styles.input}
            disabled={isLoading}
          />
          <button
            onClick={handleVerifyTicket}
            disabled={isLoading || !paymentId.trim()}
            className={styles.verifyButton}
          >
            {isLoading ? "Verifying..." : "Verify Ticket"}
          </button>
        </div>

        {message && (
          <div className={`${styles.message} ${styles[messageType]}`}>
            {message}
          </div>
        )}

        {ticket && (
          <div className={styles.ticketSection}>
            <h2 className={styles.sectionTitle}>Dinner Details</h2>

            <div className={styles.detailsGrid}>
              <div className={styles.detailItem}>
                <span className={styles.label}>Payment ID</span>
                <span className={styles.value}>
                  {ticket.paymentId.slice(0, 8)}...
                </span>
              </div>

              <div className={styles.detailItem}>
                <span className={styles.label}>Email</span>
                <span className={styles.value}>{ticket.email || "N/A"}</span>
              </div>

              {ticket.vegDinner ? (
                <div className={styles.detailItem}>
                  <span className={styles.label}>Vegetarian Dinners</span>
                  <span className={`${styles.value} ${styles.dinnerCount}`}>
                    {ticket.vegDinner}
                  </span>
                </div>
              ) : null}

              {ticket.nonVegDinner ? (
                <div className={styles.detailItem}>
                  <span className={styles.label}>Non-Veg Dinners</span>
                  <span className={`${styles.value} ${styles.dinnerCount}`}>
                    {ticket.nonVegDinner}
                  </span>
                </div>
              ) : null}

              <div className={styles.detailItem}>
                <span className={styles.label}>Food Status</span>
                <span
                  className={`${styles.value} ${ticket.foodCheckedOut ? styles.checkedOut : styles.pending}`}
                >
                  {ticket.foodCheckedOut ? "✓ Food Delivered" : "Pending"}
                </span>
              </div>

              {ticket.foodCheckedOutAt && (
                <div className={styles.detailItem}>
                  <span className={styles.label}>Delivered At</span>
                  <span className={styles.value}>
                    {new Date(ticket.foodCheckedOutAt).toLocaleString()}
                  </span>
                </div>
              )}
            </div>

            <div className={styles.actions}>
              {!ticket.foodCheckedOut ? (
                <button
                  onClick={handleCheckOutFood}
                  disabled={isLoading}
                  className={styles.checkOutButton}
                >
                  {isLoading ? "Processing..." : "Deliver Dinner Boxes"}
                </button>
              ) : (
                <div className={styles.checkedOutMessage}>
                  ✓ Dinner already delivered at{" "}
                  {new Date(ticket.foodCheckedOutAt!).toLocaleTimeString()}
                </div>
              )}

              <button
                onClick={handleClear}
                disabled={isLoading}
                className={styles.clearButton}
              >
                Clear
              </button>
            </div>
          </div>
        )}

        {!ticket && paymentId && !message && (
          <div className={styles.emptyState}>
            <p>
              Enter a payment ID and click "Verify Ticket" to see dinner details
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default FoodCheckoutInterface;
